import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import ReCAPTCHA from 'react-google-recaptcha';
import { APP_ROUTES, GOOGLE_CAPTCHA_KEY } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { UnitTypeUiModel } from '../../../shared/ui-models/unit-type.ui-model';
import {
  BookAvailabilityV2ResponseModel,
  BookOfferResponseModel
} from '../../../shared/ui-models/book.ui-model';
import { UserUIModel } from '../../../shared/ui-models/user.ui-model';
import { ReservationModel } from '../../../shared/models/reservation.model';
import { CreateReservationRequestModel } from '../../../shared/models/reservation.model';
import { ReservationSource } from '../../../shared/enums/reservation-souce.enum';
import { ReservationChannel } from '../../../shared/enums/reservation-channel.enum';
import { ReservationTypeOfStay } from '../../../shared/enums/reservation-type-of-stay.enum';
import { AdType } from '../../../shared/interfaces/attribution.interface';
import {
  isSalesPersonRequired,
  resolveAttributionChannels
} from '../../../shared/services/helpers/reservation-source-channel.helper';
import { getAllAreas } from '../../../shared/services/data/lookups.data';
import { getAllProperties } from '../../../shared/services/data/property.data';
import { getAllBrokers } from '../../../shared/services/data/broker.data';
import api from '../../../shared/services/utils/api/api.service';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { getAllCompanies } from '../../../CompanyModule/shared/services/data.service';
import { getSalesPersons } from '../../../SalesPersonsModule/data.service';
import {
  createReservation,
  getBookAvailabilityV2,
  getBookOffer
} from '../../shared/services/data.service';
import { getModuleName } from '../../shared/services/reservation.service';
import { SectionNav } from '../reservation-edit-new/components/section-nav/section-nav.component';
import { LookupOption, UnitSection } from './components/unit-section/unit-section.component';
import { DatesSection } from './components/dates-section/dates-section.component';
import {
  GuestDetails,
  GuestSection,
  isGuestComplete
} from './components/guest-section/guest-section.component';
import {
  SourceSection,
  SourceValues,
  isSourceComplete
} from './components/source-section/source-section.component';
import {
  OptionsSection,
  OptionsValues
} from './components/options-section/options-section.component';
import { SummaryRail } from './components/summary-rail/summary-rail.component';
import { CONTENT_CLASS, SECONDARY_BUTTON, SECTION_CLASS } from './reservation-create.ui';

const SECTIONS = [
  { id: 'create-unit', label: 'Unit' },
  { id: 'create-dates', label: 'Dates & price' },
  { id: 'create-guest', label: 'Guest' },
  { id: 'create-source', label: 'Source' },
  { id: 'create-options', label: 'Options' }
];

const ROUTES = APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig;
const API_DATE = 'DD/MM/YYYY';

interface OfferInputs {
  dates?: Date[];
  overridePrice?: number;
  promoCode: string;
  isAutoPromoDismissed?: boolean;
  discountAmount?: number;
  discountFees?: string[];
}

const ReservationCreate: React.FC = () => {
  const history = useHistory();

  const [areas, setAreas] = useState<LookupOption[]>([]);
  const [projects, setProjects] = useState<LookupOption[]>([]);
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [brokers, setBrokers] = useState<{ value: number; label: string }[]>([]);
  const [isBrokersLoading, setIsBrokersLoading] = useState(true);
  const [salesPersons, setSalesPersons] = useState<UserUIModel[]>([]);
  const [isSalesPersonsLoading, setIsSalesPersonsLoading] = useState(true);
  const [promotionOptions, setPromotionOptions] = useState<{ value: string; label: string }[]>([]);
  const [isPromotionsLoading, setIsPromotionsLoading] = useState(false);
  const promotionsLoadedRef = useRef(false);

  const [unitType, setUnitType] = useState<UnitTypeUiModel>();
  const [availability, setAvailability] = useState<BookAvailabilityV2ResponseModel>();
  const availabilityRequestRef = useRef(0);
  const [offerInputs, setOfferInputs] = useState<OfferInputs>({ promoCode: '' });
  const [bookOffer, setBookOffer] = useState<BookOfferResponseModel>();
  const [isOfferLoading, setIsOfferLoading] = useState(false);
  const offerRequestRef = useRef(0);
  const [room, setRoom] = useState({ id: '', name: '' });
  const [guest, setGuest] = useState<GuestDetails>();
  const [sourceValues, setSourceValues] = useState<SourceValues>({
    source: ReservationSource.CX,
    channel: ReservationChannel.BIRDNEST,
    companyId: ''
  });
  const [optionsValues, setOptionsValues] = useState<OptionsValues>({
    salesPerson: null,
    isRelatedReservation: false,
    relatedReservationId: '',
    skipEmail: false,
    isComplimentary: false
  });
  const [isCreating, setIsCreating] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    getAllAreas(getModuleName()).then((response) => {
      if (!isErrorInfo(response)) {
        setAreas(
          response.map((area) => ({
            value: area.name || '',
            label: area.name || '',
            areaId: area.id || ''
          }))
        );
      }
    });
    getAllProperties(getModuleName()).then((response) => {
      if (!isErrorInfo(response)) {
        setProjects(
          response.map((property) => ({
            value: property.id || '',
            label: property.name || '',
            areaId: property.areaId || ''
          }))
        );
      }
    });
    getAllCompanies().then((response) => {
      if (!isErrorInfo(response)) {
        setCompanies(
          response.map((company) => ({ value: company.id || '', label: company.name || '' }))
        );
      }
    });
    getAllBrokers()
      .then((response) => {
        if (!isErrorInfo(response)) {
          setBrokers(
            response
              .filter((broker) => typeof broker.id === 'number')
              .map((broker) => ({
                value: broker.id as number,
                label: broker.name || `Broker ${broker.id}`
              }))
          );
        }
      })
      .finally(() => setIsBrokersLoading(false));
    getSalesPersons()
      .then((response) => setSalesPersons(isErrorInfo(response) ? [] : response))
      .finally(() => setIsSalesPersonsLoading(false));
  }, []);

  const fetchOffer = (unitTypeId: string, inputs: OfferInputs) => {
    const previousInputs = offerInputs;
    setOfferInputs(inputs);
    if (!inputs.dates || inputs.dates.length !== 2) {
      offerRequestRef.current++;
      setBookOffer(undefined);
      setIsOfferLoading(false);
      setRoom({ id: '', name: '' });
      return;
    }
    const requestId = ++offerRequestRef.current;
    setIsOfferLoading(true);
    getBookOffer(
      unitTypeId,
      moment(inputs.dates[0]).format(API_DATE),
      moment(inputs.dates[1]).format(API_DATE),
      inputs.discountAmount,
      inputs.discountFees,
      inputs.overridePrice,
      inputs.promoCode || undefined,
      !inputs.promoCode &&
        !inputs.isAutoPromoDismissed &&
        inputs.overridePrice === undefined &&
        inputs.discountAmount === undefined
    )
      .then((response) => {
        if (requestId !== offerRequestRef.current) return;
        if (isErrorInfo(response)) {
          if (response.status === 400 && response.message) {
            setOfferInputs(previousInputs);
            birdToast.error(response.message);
          } else {
            birdToast.error('Could not calculate the price for these dates');
          }
          return;
        }
        const offer = response as BookOfferResponseModel;
        setBookOffer(offer);
        setRoom((current) =>
          offer.availableRooms?.some((item) => item.unitId === current.id)
            ? current
            : { id: '', name: '' }
        );
      })
      .finally(() => {
        if (requestId === offerRequestRef.current) setIsOfferLoading(false);
      });
  };

  const selectUnitType = (nextUnitType: UnitTypeUiModel) => {
    if (nextUnitType.id === unitType?.id) return;
    offerRequestRef.current++;
    setUnitType(nextUnitType);
    setAvailability(undefined);
    setBookOffer(undefined);
    setIsOfferLoading(false);
    setRoom({ id: '', name: '' });
    setOfferInputs({ promoCode: '' });
    const requestId = ++availabilityRequestRef.current;
    getBookAvailabilityV2(nextUnitType.id || '').then((response) => {
      if (requestId !== availabilityRequestRef.current) return;
      if (isErrorInfo(response)) {
        birdToast.error('Could not load availability for this unit');
        setAvailability({ checkInBlockedDates: [], checkOutBlockedDates: [] });
        return;
      }
      setAvailability(response);
    });
    document.getElementById('create-dates')?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadPromotions = async () => {
    if (isPromotionsLoading || promotionsLoadedRef.current) return;
    setIsPromotionsLoading(true);
    try {
      const response = await api.getPromotions();
      const promotions = response.ok ? (response.data as any)?.data : undefined;
      if (!Array.isArray(promotions)) return;
      setPromotionOptions(
        promotions
          .map((promotion: { name?: string }) => promotion.name?.trim())
          .filter((name: string | undefined): name is string => !!name)
          .map((name: string) => ({ value: name, label: name }))
      );
      promotionsLoadedRef.current = true;
    } finally {
      setIsPromotionsLoading(false);
    }
  };

  const hasDates = offerInputs.dates?.length === 2;
  const appliedPromoCode = offerInputs.promoCode || bookOffer?.promoCodeUsed || '';
  const isRelatedValid =
    !optionsValues.isRelatedReservation || !!optionsValues.relatedReservationId.trim();
  const needsSalesPerson = isSalesPersonRequired(sourceValues.source);
  const isSalesPersonValid = !needsSalesPerson || !!optionsValues.salesPerson;
  const canCreate =
    !!unitType &&
    hasDates &&
    !!bookOffer &&
    !isOfferLoading &&
    !!room.id &&
    isGuestComplete(guest) &&
    isSourceComplete(sourceValues) &&
    isRelatedValid &&
    isSalesPersonValid;

  const handleCreate = async () => {
    if (!canCreate || !unitType || !guest || !offerInputs.dates) return;
    const { typeOfStay, source, channel, companyId, brokerId } = sourceValues;
    const {
      salesPerson,
      isRelatedReservation,
      relatedReservationId,
      skipEmail,
      isComplimentary
    } = optionsValues;
    const request: CreateReservationRequestModel = {
      primaryGuestName: guest.name.trim(),
      phoneNumber: guest.phoneNumber,
      gender: guest.gender,
      email: guest.email,
      skipEmail,
      channel,
      source,
      ...resolveAttributionChannels(source, {
        marketingChannel: sourceValues.marketingChannel,
        messagingChannel: sourceValues.messagingChannel
      }),
      adType: AdType.UNKNOWN,
      typeOfStay,
      companyId: companyId || undefined,
      brokerId: typeOfStay === ReservationTypeOfStay.BROKER ? brokerId : undefined,
      salesPerson: salesPerson ? `${salesPerson.email} (${salesPerson.name})` : '',
      relatedReservationId: isRelatedReservation ? Number(relatedReservationId.trim()) : undefined,
      isComplimentary,
      unitTypeId: Number(unitType.id),
      roomId: room.id,
      recurringPayment: bookOffer?.recurringPayment,
      oneTimePayment: bookOffer?.oneTimePayment,
      rateCard: bookOffer?.rateCard,
      discountAmount: offerInputs.discountAmount,
      discountFees: offerInputs.discountFees,
      promoCodeUsed: bookOffer?.promoCodeUsed,
      checkInDate: moment(offerInputs.dates[0]).format(API_DATE),
      checkOutDate: moment(offerInputs.dates[1]).format(API_DATE),
      ...(guest.id && { userId: guest.id })
    };

    setIsCreating(true);
    try {
      const token = (await recaptchaRef.current?.executeAsync()) || undefined;
      const promise = createReservation({ ...request, token }).then((response) => {
        if (isErrorInfo(response)) throw response;
        return response as ReservationModel;
      });
      birdToast.promise(promise, {
        errorFailedMessage: 'Failed to create reservation',
        successMessage: 'Reservation created successfully',
        loadingMessage: 'Creating reservation...',
        errorPermissionMessage: 'You do not have permission to create reservation'
      });
      const reservation = await promise;
      history.push(
        reservation?.id
          ? ROUTES.EDIT.FULL_PATH.replace(':id', String(reservation.id))
          : ROUTES.LIST.FULL_PATH
      );
    } catch {
      setIsCreating(false);
      recaptchaRef.current?.reset();
    }
  };

  return (
    <div className="min-h-[calc(100vh-45px)] bg-neutral-0 font-gilroy-regular text-neutral-500">
      <div className={`${CONTENT_CLASS} pt-[32px] pb-[24px]`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-[16px]">
          <h1 className="m-0 text-[clamp(26px,2.6vw,40px)] leading-tight font-gilroy-bold text-neutral-500">
            New reservation
          </h1>
          <div className="flex items-center gap-[6px] flex-wrap shrink-0">
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => history.push(ROUTES.LIST.FULL_PATH)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <SectionNav data={{ items: SECTIONS }} />

      <div className={`${CONTENT_CLASS} pb-[80px]`}>
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-x-[48px]">
          <div className="divide-y divide-gray-200 min-w-0">
            <section id="create-unit" className={SECTION_CLASS}>
              <UnitSection
                data={{ areas, projects, selectedUnitType: unitType }}
                eventHandlers={{ onSelectUnitType: selectUnitType }}
              />
            </section>
            <section id="create-dates" className={SECTION_CLASS}>
              <DatesSection
                key={unitType?.id || 'none'}
                data={{
                  unitType,
                  availability,
                  dates: offerInputs.dates,
                  bookOffer,
                  roomId: room.id,
                  overridePrice: offerInputs.overridePrice,
                  promoCode: appliedPromoCode,
                  discountAmount: offerInputs.discountAmount,
                  discountFees: offerInputs.discountFees,
                  promotion: { options: promotionOptions, isLoading: isPromotionsLoading }
                }}
                eventHandlers={{
                  onDatesChange: (dates) =>
                    unitType && fetchOffer(unitType.id || '', { ...offerInputs, dates }),
                  onRoomChange: (id, name) => setRoom({ id, name }),
                  onOverridePriceChange: (overridePrice) =>
                    unitType && fetchOffer(unitType.id || '', { ...offerInputs, overridePrice }),
                  onPromoCodeChange: (promoCode) =>
                    unitType &&
                    fetchOffer(unitType.id || '', {
                      ...offerInputs,
                      promoCode,
                      isAutoPromoDismissed: !promoCode
                    }),
                  onPromoMenuOpen: loadPromotions,
                  onDiscountChange: (discountAmount, discountFees) =>
                    unitType &&
                    fetchOffer(unitType.id || '', { ...offerInputs, discountAmount, discountFees })
                }}
              />
            </section>
            <section id="create-guest" className={SECTION_CLASS}>
              <GuestSection data={{ guest }} eventHandlers={{ onGuestChange: setGuest }} />
            </section>
            <section id="create-source" className={SECTION_CLASS}>
              <SourceSection
                data={{ values: sourceValues, companies, brokers, isBrokersLoading }}
                eventHandlers={{ onChange: setSourceValues }}
              />
            </section>
            <section id="create-options" className={SECTION_CLASS}>
              <OptionsSection
                data={{
                  values: optionsValues,
                  salesPersons,
                  isSalesPersonsLoading,
                  isSalesPersonRequired: needsSalesPerson
                }}
                eventHandlers={{ onChange: setOptionsValues }}
              />
            </section>
          </div>

          <aside className="pb-[40px] xl:py-[40px]">
            <SummaryRail
              data={{
                unitType,
                roomName: room.name,
                dates: offerInputs.dates,
                guestName: guest?.name || '',
                bookOffer,
                isOfferLoading,
                canCreate,
                isCreating
              }}
              eventHandlers={{ onCreate: handleCreate }}
            />
          </aside>
        </div>
      </div>

      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={GOOGLE_CAPTCHA_KEY || ''} />
    </div>
  );
};

export default ReservationCreate;
